import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Card,
  CardBody,
  Flex,
  HStack,
  Stack,
  useClipboard,
} from '@chakra-ui/react';
import { magnifyValue } from '@diamond/shared/decimal-util-web';
import {
  BankIcon,
  Button,
  Divider,
  Heading,
  Icon,
  Text,
} from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import { InfoOutlined } from '@mui/icons-material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CreditCardIcon from '@mui/icons-material/CreditCard';

export interface MetodePembayaranProps {
  paymentMethod: string;
  rekening: string;
  accountHolder: string;
  bank: string;
  totalTagihanAkhir: number;
  totalDiff: number;
  onCopyValue: (value: string) => void;
  transactionId?: string;
  orderStatus?: string;
}

export function CardMetodePembayaran({
  paymentMethod,
  rekening,
  accountHolder,
  bank,
  totalTagihanAkhir,
  totalDiff,
  onCopyValue,
  transactionId,
  orderStatus,
}: MetodePembayaranProps) {
  const {
    onCopy: onCopyRekening,
    value: valueRekening,
    hasCopied: hasCopiedRekening,
  } = useClipboard(String(rekening));
  const {
    onCopy: onCopyTotal,
    value: valueTotal,
    hasCopied: hasCopiedTotal,
  } = useClipboard(String(totalTagihanAkhir));
  const {
    onCopy: onCopyBeritaTransfer,
    value: valueBeritaTransfer,
    hasCopied: hasCopiedBeritaTransfer,
  } = useClipboard(String(transactionId ?? ''));

  const totalTagihanAkhirMagnified = magnifyValue(totalTagihanAkhir, 'idr');

  return (
    <Card
      shadow="none"
      borderRadius="md"
      borderWidth="1px"
      borderColor="rgba(217, 217, 217, 1)"
    >
      <CardBody p={0}>
        <Accordion
          allowToggle
          defaultIndex={orderStatus === 'processed' ? undefined : [0]}
        >
          <AccordionItem border="none">
            <AccordionButton
              p={{ base: 3, md: 4 }}
              _hover={{ bg: 'gray.50' }}
              data-testid="accordion-button-payment-method"
            >
              <Flex alignItems="center" gap="2" flex={1}>
                <Icon as={CreditCardIcon} color="blue" fontSize="32px" />
                <Heading
                  fontSize={{ base: 'lg', md: 'xl' }}
                  fontWeight="semibold"
                >
                  Metode Pembayaran
                </Heading>
              </Flex>
              <AccordionIcon />
            </AccordionButton>

            <AccordionPanel p={{ base: 3, md: 4 }} pt={0}>
              <Stack>
                <Stack py={2}>
                  <Divider
                    w="330px"
                    borderColor="#D9D9D9"
                    borderWidth="1px"
                    borderStyle="dashed"
                    opacity={1}
                    sx={{
                      borderTopStyle: 'dashed',
                      borderTopWidth: '1px',
                      borderTopColor: '#D9D9D9',
                      backgroundImage: 'none',
                    }}
                  />
                </Stack>
                {paymentMethod === 'cash_on_delivery' ? (
                  <Stack
                    direction={{ base: 'column', md: 'row' }}
                    justifyContent="space-between"
                  >
                    <HStack>
                      <Box justifyContent="center" alignItems="center">
                        <Flex>
                          <Text fontSize="2xl" fontWeight="bold">
                            COD
                          </Text>
                          <Box
                            px="2"
                            border="2px"
                            ml="2"
                            pt="2px"
                            rounded="lg"
                            borderColor="#7928CA"
                            w={{ base: '-moz-fit-content', md: 'auto' }}
                            alignSelf="normal"
                          >
                            <Text
                              as="i"
                              fontWeight="bold"
                              bgGradient="linear(to-l, #7928CA, #FF0080)"
                              bgClip="text"
                              fontSize={{ base: '8px', md: '10px' }}
                            >
                              CASH ON DELIVERY
                            </Text>
                          </Box>
                        </Flex>
                        <Text
                          fontSize={{ base: '12px', md: '14px' }}
                          fontWeight="medium"
                          as="span"
                        >
                          Pembayaran saat penerimaan barang
                        </Text>
                      </Box>
                    </HStack>
                    <Box textAlign={{ base: 'start', md: 'end' }}>
                      <Text color="gray.75">Total Tagihan</Text>
                      <Text fontWeight="semibold">
                        {buyerCurrencyFormatter(
                          totalTagihanAkhirMagnified.magnifiedValue
                        )}
                      </Text>
                    </Box>
                  </Stack>
                ) : paymentMethod === 'bank_transfer' ? (
                  <Stack>
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      w={{ base: '94px', md: '145px' }}
                    >
                      <BankIcon bank={bank} inOrderDetail />
                    </Flex>
                    <HStack justifyContent="space-between">
                      <Box w="full">
                        <Stack spacing="0">
                          <Text
                            fontSize={{ base: 'xs', md: 'md' }}
                            color="gray.75"
                          >
                            No. Rekening:
                          </Text>
                          <Text
                            aria-label="nomor-rekening"
                            fontWeight="medium"
                            fontSize={{ base: 'xs', md: 'xl' }}
                          >
                            {rekening}
                          </Text>
                        </Stack>
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight="semibold"
                        >
                          {`a/n ${accountHolder}`}
                        </Text>
                      </Box>
                      <Button
                        id="copy-rekening"
                        data-testid="copy-rekening"
                        rightIcon={<ContentCopyRoundedIcon />}
                        variant="ghost"
                        color="blue"
                        onClick={() => {
                          onCopyRekening();
                          onCopyValue(valueRekening);
                        }}
                      >
                        <Box as="span" fontSize="14px">
                          {hasCopiedRekening ? 'Disalin' : 'Salin'}
                        </Box>
                      </Button>
                    </HStack>
                    <HStack justifyContent="space-between">
                      <Box w="full">
                        <Text
                          fontSize={{ base: 'xs', md: 'md' }}
                          color="gray.75"
                        >
                          Total Tagihan
                        </Text>
                        <Text
                          aria-label="total-tagihan"
                          fontWeight="bold"
                          fontSize={{ base: 'xl', md: '2xl' }}
                        >
                          {buyerCurrencyFormatter(
                            totalTagihanAkhirMagnified.magnifiedValue
                          )}
                        </Text>
                      </Box>
                      <Button
                        id="copy-total"
                        rightIcon={<ContentCopyRoundedIcon />}
                        variant="ghost"
                        color="blue"
                        onClick={() => {
                          onCopyTotal();
                          onCopyValue(valueTotal);
                        }}
                      >
                        <Box as="span" fontSize="14px">
                          {hasCopiedTotal ? 'Disalin' : 'Salin'}
                        </Box>
                      </Button>
                    </HStack>
                    <HStack justifyContent="space-between">
                      <Box w="full">
                        <Text
                          fontSize={{ base: 'xs', md: 'md' }}
                          color="gray.75"
                        >
                          Berita Transfer
                        </Text>
                        <Stack spacing="0">
                          <Text
                            aria-label="berita-transfer"
                            fontWeight="medium"
                            color="rgba(19, 20, 21, 1)"
                            fontSize={{ base: 'xl', md: '2xl' }}
                          >
                            {transactionId ?? '-'}
                          </Text>
                          <Text
                            fontSize={{ base: 'xx-small', md: 'xs' }}
                            fontWeight="medium"
                            color="gray.75"
                            as="i"
                          >
                            Cantumkan pada berita atau catatan transfer untuk
                            verifikasi lebih cepat.
                          </Text>
                        </Stack>
                      </Box>
                      <Button
                        id="copy-berita-transfer"
                        rightIcon={<ContentCopyRoundedIcon />}
                        variant="ghost"
                        color="blue"
                        onClick={() => {
                          onCopyBeritaTransfer();
                          onCopyValue(valueBeritaTransfer);
                        }}
                      >
                        <Box as="span" fontSize="14px">
                          {hasCopiedBeritaTransfer ? 'Disalin' : 'Salin'}
                        </Box>
                      </Button>
                    </HStack>
                  </Stack>
                ) : (
                  <Stack
                    direction={{ base: 'column', md: 'row' }}
                    justifyContent="space-between"
                  >
                    <Box justifyContent="center" alignItems="center">
                      <Flex>
                        <Text fontSize="2xl" fontWeight="bold">
                          TOP
                        </Text>
                        <Box
                          px="2"
                          border="2px"
                          ml="2"
                          pt="2px"
                          rounded="lg"
                          borderColor="aqua"
                          w={{ base: '-moz-fit-content', md: 'auto' }}
                          alignSelf="normal"
                        >
                          <Text
                            as="i"
                            fontWeight="bold"
                            bgGradient="linear(to-l, #00A0E8, #85EBFF )"
                            bgClip="text"
                            fontSize={{ base: '8px', md: '10px' }}
                          >
                            TERM OF PAYMENT
                          </Text>
                        </Box>
                      </Flex>
                      <Text
                        fontSize={{ base: '12px', md: '14px' }}
                        fontWeight="medium"
                        as="span"
                      >
                        (Term of payment)
                      </Text>
                    </Box>
                    <Box textAlign={{ base: 'start', md: 'end' }}>
                      <Text color="gray.75">Total Tagihan</Text>
                      <Text fontWeight="semibold">
                        {buyerCurrencyFormatter(
                          totalTagihanAkhirMagnified.magnifiedValue
                        )}
                      </Text>
                    </Box>
                  </Stack>
                )}
                {totalDiff !== 0 && (
                  <Flex
                    gap="2"
                    alignItems="center"
                    bg="#FEF3C7"
                    borderRadius="8px"
                    px={{ base: '2', md: '4' }}
                    py="2"
                  >
                    <Icon as={InfoOutlined} color="#F97316" />
                    <Text
                      fontSize={{ base: '2xs', md: 'sm' }}
                      fontWeight="bold"
                    >
                      {totalDiff < 0
                        ? `Potensi deposit ${buyerCurrencyFormatter(
                            Math.abs(totalDiff)
                          )}`
                        : `Potensi kurang bayar ${buyerCurrencyFormatter(
                            totalDiff
                          )}`}
                      <Text
                        as="span"
                        fontSize={{ base: '2xs', md: 'sm' }}
                        fontWeight="normal"
                      >
                        {' '}
                        karena perubahan stok atau produk.
                      </Text>
                    </Text>
                  </Flex>
                )}
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </CardBody>
    </Card>
  );
}

export default CardMetodePembayaran;
